import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Sparkles, Compass } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo93CriterioPropio() {
    const [pensamientos, setPensamientos] = useState('');
    const [decisiones, setDecisiones] = useState('');
    const [interpretacion, setInterpretacion] = useState('');
    const [baseAccion, setBaseAccion] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [nivelAutonomia, setNivelAutonomia] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/criterio_propio`, { withCredentials: true });
                if (data.data) {
                    if (data.data.pensamientos) setPensamientos(data.data.pensamientos);
                    if (data.data.decisiones) setDecisiones(data.data.decisiones);
                    if (data.data.interpretacion) setInterpretacion(data.data.interpretacion);
                    if (data.data.baseAccion) setBaseAccion(data.data.baseAccion);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'criterio_propio', data: { pensamientos, decisiones, interpretacion, baseAccion } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [pensamientos, decisiones, interpretacion, baseAccion]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [pensamientos, decisiones, interpretacion, baseAccion, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (pensamientos.trim().length > 0 && decisiones.trim().length > 0 && interpretacion.trim().length > 0 && baseAccion.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/criterio-propio/submit`, {
                pensamientos, decisiones, interpretacion, base_accion: baseAccion
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setNivelAutonomia(data.nivel_autonomia);
            setSubmitted(true);
            toast.success('Criterio propio completado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#7A9BA8] to-[#5F8494] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Compass className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="criterio-propio-success-title">Criterio propio definido</h2>
                    <p className="text-[#7A9BA8] font-medium mb-4">Ahora decides desde tu sistema, no desde el impulso</p>
                    {nivelAutonomia && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Autonomía: {nivelAutonomia}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#7A9BA8] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#7A9BA8] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="criterio_propio" chapter={9} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-criterio-propio">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/usuario" className="flex items-center gap-2 text-[#3E5F6E] hover:text-[#2F4A57]"><ArrowLeft className="h-5 w-5" /><span className="text-sm font-medium">Volver</span></Link>
                    <span className="text-xs text-[#9AA5B1]">{savingDraft ? 'Guardando...' : 'Borrador guardado'}</span>
                </div>
            </header>
            <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2"><div className="w-10 h-10 bg-[#3E5F6E] rounded-xl flex items-center justify-center"><Compass className="h-5 w-5 text-white" /></div><span className="text-xs font-semibold text-[#9AA5B1] uppercase tracking-wider">Capítulo 9 · Módulo 9.3</span></div>
                    <h1 className="text-2xl sm:text-3xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Criterio propio</h1>
                    <p className="text-[#616E7C]">Desarrolla autonomía decisional interna basada en tu sistema. Decide desde la claridad, no desde el impulso.</p>
                </div>
                <div className="space-y-6">
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Pensamientos</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Cómo piensas ahora cuando enfrentas una situación difícil?</p>
                        <Textarea value={pensamientos} onChange={e => setPensamientos(e.target.value)} placeholder="Cuando enfrento algo difícil, pienso..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="criterio-pensamientos" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Decisiones</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Cómo tomas decisiones ahora? ¿En qué te basas?</p>
                        <Textarea value={decisiones} onChange={e => setDecisiones(e.target.value)} placeholder="Mis decisiones ahora se basan en..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="criterio-decisiones" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Interpretación</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Cómo interpretas los resultados de tus acciones?</p>
                        <Textarea value={interpretacion} onChange={e => setInterpretacion(e.target.value)} placeholder="Interpreto mis resultados como..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="criterio-interpretacion" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Base para la acción</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Desde qué criterio interno actúas? ¿Qué te guía?</p>
                        <Textarea value={baseAccion} onChange={e => setBaseAccion(e.target.value)} placeholder="Mi criterio para actuar es..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="criterio-base-accion" />
                    </div>
                    <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="w-full bg-[#3E5F6E] hover:bg-[#2F4A57] text-white py-3 rounded-xl" data-testid="submit-criterio-propio">
                        {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analizando...</> : 'Completar criterio propio'}
                    </Button>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
